import {
    BottomNavigation,
    BottomNavigationAction,
    Paper,
    useMediaQuery
} from '@mui/material'
import PropTypes from 'prop-types'
import { useLocation, useNavigate } from 'react-router-dom'
import { useFeatureFlags } from '@/features/feature-flags/useFeatureFlags'
import useAuth from '@/hooks/useAuth'
import { bottomNavItems } from '@/navConfig'
import { canAccessNavEntry } from '../nav-access'

export function BottomTabBar({ onMoreOpen }) {
    const { pathname } = useLocation()
    const navigate = useNavigate()
    const { isAuthenticated, user, activeOrg } = useAuth()
    // Phones only (the sidebar takes over from sm). Not mounting it on larger
    // screens also keeps its fixed Paper out of desktop layouts entirely,
    // rather than relying on display:none alone.
    const isPhone = useMediaQuery((theme) => theme.breakpoints.down('sm'), {
        noSsr: true
    })
    const { flags: featureFlags } = useFeatureFlags()
    const navAccess = {
        isAuthenticated,
        isStaff: user?.isStaff === true,
        user,
        activeOrg,
        featureFlags
    }
    const visibleBottomNavItems = bottomNavItems.filter((item) =>
        canAccessNavEntry(item, navAccess)
    )

    const activeTab =
        visibleBottomNavItems.find(
            (item) =>
                item.path &&
                (item.path === '/'
                    ? pathname === '/'
                    : pathname.startsWith(item.path))
        )?.id ?? false

    const handleChange = (_, newValue) => {
        const item = visibleBottomNavItems.find((i) => i.id === newValue)
        if (!item) return
        if (item.path === null) {
            onMoreOpen()
        } else {
            navigate(item.path)
        }
    }

    if (!isPhone) {
        return null
    }

    return (
        <Paper
            sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 'appBar',
                display: { xs: 'block', sm: 'none' },
                // Clear the iOS home indicator / Android gesture bar. The
                // Paper's own background fills the inset, so the bar reads as
                // one surface to the screen edge. Requires viewport-fit=cover
                // (index.html / templates/base.html); without it the env() is 0.
                pb: 'env(safe-area-inset-bottom, 0px)',
                pl: 'env(safe-area-inset-left, 0px)',
                pr: 'env(safe-area-inset-right, 0px)'
            }}
            elevation={3}
        >
            <BottomNavigation
                component="nav"
                aria-label="Primary"
                value={activeTab}
                onChange={handleChange}
                showLabels
            >
                {visibleBottomNavItems.map((item) => (
                    <BottomNavigationAction
                        key={item.id}
                        label={item.label}
                        value={item.id}
                        icon={item.icon}
                    />
                ))}
            </BottomNavigation>
        </Paper>
    )
}

BottomTabBar.propTypes = {
    onMoreOpen: PropTypes.func.isRequired
}
